import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Briefcase, 
  GraduationCap, 
  Building2, 
  TrendingUp,
  Users,
  Globe,
  ChevronDown,
  MapPin,
  Calendar
} from "lucide-react";

const EXPERIENCE_DATA = [
  {
    id: 1,
    period: "2021 - Mars 2026",
    company: "YADES Immobilier SA & AMMA IMMO",
    role: "Fondateur et associé-gérant / administrateur",
    location: "Suisse romande",
    icon: Building2,
    color: "#B07D62",
    highlights: [
      "Estimation, courtage de tout type de bien-fonds",
      "Étude de faisabilité immobilière",
      "Pilotage de promotions immobilières",
      "Acquisition des promotions immobilières dans cantons de Vaud, Berne, Valais et Fribourg",
      "Création de diverses sociétés de détention de promotions immobilières",
      "Création et gestion des sociétés opérationnelles actives dans la promotion (YADES) et dans le courtage (AMMA)",
    ],
  },
  {
    id: 2,
    period: "2019 - 2023",
    company: "USPI Formation",
    role: "Responsable des formations Courtage",
    location: "Lausanne",
    icon: GraduationCap,
    color: "#415A77",
    highlights: [
      "Responsable des formations Courtage auprès de l'USPI Formation à Lausanne",
      "Création et adaptation des cours, tous niveaux confondus, destinés aux professionnels actifs dans le courtage en immobilier",
      "Sélection des enseignants",
    ],
  },
  {
    id: 3,
    period: "2017 - 2021",
    company: "Cogestim",
    role: "Courtier",
    location: "Yverdon-les-Bains",
    icon: Briefcase,
    color: "#B07D62",
    highlights: [
      "Recherche de foncier à développer",
      "Estimation, courtage de tout type de bien-fonds",
      "Étude de faisabilité",
      "Pilotage de promotions immobilières",
    ],
  },
  {
    id: 4,
    period: "2016",
    company: "Brolliet",
    role: "Responsable courtage et développement - Canton de Vaud",
    location: "Lausanne",
    icon: TrendingUp,
    color: "#415A77",
    highlights: [
      "Recherche de foncier à développer",
      "Estimation, courtage de tout type de bien-fonds",
      "Étude de faisabilité",
      "Mise en place de la succursale de Lausanne",
    ],
  },
  {
    id: 5,
    period: "2012 - 2015",
    company: "Régie immobilière Piguet",
    role: "Sous-Directeur & Courtier",
    location: "Yverdon-les-Bains",
    icon: Building2,
    color: "#B07D62",
    highlights: [
      "Estimation, courtage de tout type de bien-fonds",
      "Recherche de foncier à développer",
      "Étude de faisabilité",
      "Pilotage de promotions d'habitation",
    ],
  },
  {
    id: 6,
    period: "2006 - 2012",
    company: "Westcore Europe",
    role: "Asset Manager & Responsable des acquisitions",
    location: "Suisse & Autriche",
    icon: Globe,
    color: "#415A77",
    highlights: [
      "Recherche de foncier à mettre en valeur",
      "Établissement de business plan",
      "Mise en valeur de bien-fonds commerciaux",
      "Négociation transaction",
      "Création et mise en place de la société",
    ],
  },
  {
    id: 7,
    period: "2001 - 2006",
    company: "UBS",
    role: "Fondé de pouvoir et conseiller à la clientèle",
    location: "Genève",
    icon: Users,
    color: "#B07D62",
    highlights: [
      "Analyse de portefeuille",
      "Acquisition de nouveaux clients",
      "Conseil en placement",
      "Assistanat aux conseillers à la clientèle",
      "Stage allround dans six départements",
    ],
  },
];

function TimelineCard({ item, index, isExpanded, onToggle }) {
  const Icon = item.icon;
  
  return (
    <motion.div
      initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className="relative"
    >
      {/* Timeline connector */}
      <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[#B07D62]/50 via-[#415A77]/30 to-transparent hidden md:block" />
      
      <div className={`flex items-start gap-8 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
        {/* Content */}
        <motion.div
          className="flex-1 glass-panel rounded-2xl p-6 cursor-pointer group hover:bg-[#415A77]/10 transition-all duration-500"
          onClick={onToggle}
          whileHover={{ scale: 1.02 }}
          style={{ borderColor: `${item.color}20` }}
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 group-hover:scale-110"
                style={{ backgroundColor: `${item.color}15`, border: `1px solid ${item.color}30` }}
              >
                <Icon size={24} style={{ color: item.color }} />
              </div>
              <div>
                <h3 className="font-['Playfair_Display'] text-xl text-[#FAFAF8] group-hover:text-[#B07D62] transition-colors">
                  {item.company}
                </h3>
                <p className="text-[#B07D62] text-sm font-medium">{item.role}</p>
              </div>
            </div>
            <motion.div
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ duration: 0.3 }}
              className="text-[#415A77]"
            >
              <ChevronDown size={20} />
            </motion.div>
          </div>
          
          {/* Meta */}
          <div className="flex flex-wrap gap-4 text-sm text-[#FAFAF8]/60 mb-4">
            <span className="flex items-center gap-1">
              <Calendar size={14} />
              {item.period}
            </span>
            <span className="flex items-center gap-1">
              <MapPin size={14} />
              {item.location}
            </span>
          </div>
          
          {/* Expandable content */}
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <ul className="space-y-2 pt-4 border-t border-[#415A77]/20">
                  {item.highlights.map((highlight, i) => (
                    <motion.li
                      key={i}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.05 }}
                      className="flex items-start gap-2 text-sm text-[#FAFAF8]/80"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-[#B07D62] mt-2 flex-shrink-0" />
                      {highlight}
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
        
        {/* Timeline dot - desktop only */}
        <div className="hidden md:flex items-center justify-center w-16 flex-shrink-0">
          <motion.div
            className="w-4 h-4 rounded-full border-2"
            style={{ borderColor: item.color, backgroundColor: `${item.color}30` }}
            whileHover={{ scale: 1.5 }}
          />
        </div>
        
        {/* Spacer for alternating layout */}
        <div className="hidden md:block flex-1" />
      </div>
    </motion.div>
  );
}

export default function TimelineSection() {
  const [expandedId, setExpandedId] = useState(1);

  return (
    <div className="min-h-screen py-24 px-4 md:px-8 relative">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0D1B2A] to-transparent pointer-events-none" />
      
      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="section-label mb-4">Mon Parcours</p>
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl lg:text-6xl text-[#FAFAF8] mb-6">
            20 ans d'expertise
          </h2>
          <p className="text-[#FAFAF8]/70 max-w-2xl mx-auto text-lg">
            Un parcours riche en expériences, du conseil bancaire à la promotion immobilière, 
            en passant par le courtage et la formation.
          </p>
        </motion.div>

        {/* Experience Timeline */}
        <div className="space-y-8">
          {EXPERIENCE_DATA.map((item, index) => (
            <TimelineCard
              key={item.id}
              item={item}
              index={index}
              isExpanded={expandedId === item.id}
              onToggle={() => setExpandedId(expandedId === item.id ? null : item.id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
